import React, { useCallback, useEffect } from 'react';
import useReplication from 'new/components/flows/types/replication/useReplication';
import useResourceSetup from 'new/components/flows/types/replication/CreateReplicationFlow/ResourceSetup/useResourceSetup';
import { Stack } from 'new/elements/Stack';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { FileSystem } from 'new/components/flows/configure/FileSystem';
import { CONNECTION_CATEGORY_DATABASE } from 'js/constants/connectionCategories';
import Database from 'new/components/flows/configure/Database';
import { FORM_TYPE_CREATE_REPLICATION } from 'js/constants/formTypes';
import flowTypes from 'new/components/flows/types/flowTypes';
import { useForm } from 'new/elements/Form/useForm';
import useRequests from 'new/state/request/repository/useRequests';
import { CustomUserConfirmation } from 'js/components/route_utils/UserConfirmation';

export const ResourceSetup = () => {
  const { nextPage } = useForm();
  const {
    selectedConnection,
    selectedBucket,
    credentialId,
    source,
    nexset,
    sink,
    sinkConfig,
    connectionCategory,
    connectionType,
    sourceConfig,
    table,
    isNewTable,
    isEditMode,
  } = useReplication();
  const {
    onSourceConfigChanged,
    allowCustomPrefixEditor,
    nexsetSchema,
    initialLoading,
    isRedirecting,
    onSinkConfigChanged,
    onBucketSelected,
    onTableSelected,
    onSinkMappingChanged,
    onSaveAdvancedMode,
    // SaveConfirmation modal props
    showSaveConfirmation,
    handleSaveConfirmation,
    onCloseSaveConfirmation,
  } = useResourceSetup();
  const { showError } = useRequests();
  const onError = useCallback((msg: string) => showError(msg), []);

  if (initialLoading || isRedirecting) {
    return (
      <Stack padding="50px">
        <CenteredLoader />
      </Stack>
    );
  }

  if (!connectionType) return null;
  if (connectionType === 'SINK' && !sinkConfig) return null;
  if (connectionType === 'SOURCE' && !sourceConfig) return null;

  if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
    return (
      <Database
        canCreateTable={false}
        flowType={flowTypes.replication}
        nexset={nexset}
        sink={sink}
        formType={FORM_TYPE_CREATE_REPLICATION}
        selectedConnection={selectedConnection}
        nexsetSchema={nexsetSchema}
        selectedBucket={selectedBucket}
        selectedTable={table}
        onSinkConfigChanged={onSinkConfigChanged}
        onTableSelected={onTableSelected}
        onSourceConfigChanged={onSourceConfigChanged}
        onSinkMappingUpdated={onSinkMappingChanged}
        sinkConfig={sinkConfig}
        isNewTable={isNewTable}
        isEditMode={isEditMode}
        credentialId={credentialId}
        source={source}
        connectionType={connectionType}
        sourceConfig={sourceConfig}
        onSaveAdvancedMode={onSaveAdvancedMode}
        nextFormStage={nextPage}
      />
    );
  }

  return (
    <>
      <FileSystem
        flowType={flowTypes.replication}
        connectionType={connectionType}
        sink={sink}
        formType={FORM_TYPE_CREATE_REPLICATION}
        source={source}
        nexset={nexset}
        nexsetSchema={nexsetSchema}
        selectedBucket={selectedBucket}
        allowCustomPrefixEditor={allowCustomPrefixEditor}
        selectedConnection={selectedConnection}
        credentialId={credentialId}
        sinkConfig={sinkConfig}
        sourceConfig={sourceConfig}
        onBucketSelected={onBucketSelected}
        onSourceConfigChanged={onSourceConfigChanged}
        onSinkConfigChanged={onSinkConfigChanged}
        onSaveAdvancedMode={onSaveAdvancedMode}
        nextFormStage={nextPage}
        onError={onError}
        shouldShowLoading={false}
      />

      {/* Save Confirmation Modal */}
      {showSaveConfirmation && (
        <CustomUserConfirmation
          title="Save Changes to Active Source"
          message="You've made changes to an active source. Choose how you want to apply these changes:"
          cancelButtonLabel="Cancel"
          okButtonLabel="Save for Next Run"
          yesWithChangesButtonLabel="Save & Run Now"
          onConfirm={() => {
            handleSaveConfirmation(true);
          }}
          onClose={onCloseSaveConfirmation}
          onRunWithIngestion={() => {
            handleSaveConfirmation(false);
          }}
        />
      )}
    </>
  );
};

ResourceSetup.formConfig = {
  title: 'Configure',
};
